/* eslint-disable @typescript-eslint/no-unused-vars */
import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import * as Sentry from '@sentry/node'
import { useEffect, useState } from 'react'
import { api } from '#app/utils/api'
import {
	type CleaningOrderRating,
	type CleaningProvider,
} from '#app/utils/api/types'

const formatDate = (dateString: string) => {
	const monthNames = [
		'leden',
		'únor',
		'březen',
		'duben',
		'květen',
		'červen',
		'červenec',
		'srpen',
		'září',
		'říjen',
		'listopad',
		'prosinec',
	]
	let date = new Date(dateString)
	return `${date.getDate()}. ${monthNames[date.getMonth()]}`
}

export const loader = async ({ params }: LoaderFunctionArgs) => {
	const { data: providerData } = await api.getProvider(params.id as string)
	const { data: ratingsData } = await api.getProviderRatings(
		0,
		50,
		params.id as string,
	)

	if ('errors' in ratingsData || 'errors' in providerData) {
		Sentry.captureException(
			new Error(
				'Failed to fetch ratings for provider, defensive fallback in place.',
			),
		)
		return json({
			provider: {} as CleaningProvider,
			ratings: [] as CleaningOrderRating[],
		})
	}

	return json({
		provider: providerData as CleaningProvider,
		ratings: ratingsData.data
			.map(rating => ({
				...rating.rating,
				customerName: rating.customerName,
			}))
			.filter(rating => !!rating.comment) as CleaningOrderRating[],
	})
}

export default function ProviderDetailRoute() {
	const { provider, ratings } = useLoaderData<typeof loader>()

	const [imgSrc, setImgSrc] = useState(api.getProviderImageUrl(provider._id))

	useEffect(() => {
		setImgSrc(api.getProviderImageUrl(provider._id))
	}, [provider._id])

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col items-center gap-5 lg:flex-row lg:gap-2.5">
				<img
					src={imgSrc}
					alt={provider.businessName}
					onError={() => setImgSrc('/img/cleaning-providers/default.png')}
					className="aspect-square w-36 rounded-full object-cover lg:w-16"
				/>
				<div className="text-center text-lg font-semibold leading-8  lg:text-start lg:text-[22px] lg:font-bold lg:leading-7">
					{provider.businessName}
				</div>
			</div>
			{!!provider.description && (
				<div className="mt-4 max-w-md self-center text-center text-sm leading-6 text-[#75809B] lg:mt-2 lg:self-start lg:text-start lg:leading-5">
					{provider.description}
				</div>
			)}
			<div className="mt-10 inline-flex justify-center px-3 py-2 font-medium text-primary lg:mt-2 lg:max-w-fit lg:justify-normal lg:rounded-md lg:bg-izzy-light-blue">
				<div className="lg:font-bold">Průměrné hodnocení:</div>
				<img
					width={13.87}
					height={13}
					src="/img/star.svg"
					alt="hvězda"
					className="ml-1.5 mr-1"
				/>
				<div>{(provider.rating?.average ?? 0).toFixed(1)}</div>
			</div>
			<>
				<div className="mt-10 text-center text-sm font-semibold leading-6 text-primary lg:mt-4 lg:text-start">
					Detailní hodnocení
				</div>
				<div className="mt-4 flex flex-col gap-4 lg:mt-2 lg:flex-row lg:gap-2.5">
					<RatingCard
						title="Komunikace"
						rating={(provider.rating?.average ?? 0).toFixed(1)}
					/>
					<RatingCard
						title="Kvalita úklidu"
						rating={(provider.rating?.average ?? 0).toFixed(1)}
					/>
					<RatingCard
						title="Celkové hodnocení"
						rating={(provider.rating?.average ?? 0).toFixed(1)}
					/>
				</div>
				<RatingCard
					hideStar
					title="Počet hodnocení"
					rating={provider.rating?.count.toString() ?? '0'}
				/>
			</>

			{!!ratings.length && (
				<div className="mt-10 text-center text-sm font-semibold leading-6 text-primary lg:mt-4 lg:text-start">
					Recenze zákazníků
				</div>
			)}
			<div className="mb-10 mt-4 flex max-w-3xl flex-col gap-3 self-center lg:mb-0 lg:mt-2 lg:w-full lg:max-w-none lg:self-start">
				{ratings.map(rating => (
					<Review
						key={rating.recordedAt}
						date={formatDate(rating.recordedAt ?? '')}
						text={rating.comment ?? ''}
						communication={rating.communication ?? 0}
						quality={rating.quality ?? 0}
						overall={rating.overall ?? 0}
						customerName={rating.customerName ?? ''}
					/>
				))}
			</div>
		</div>
	)
}

const RatingCard = ({
	title,
	rating,
	hideStar = false,
}: {
	title: string
	rating: string
	hideStar?: boolean
}) => (
	<div className="flex grow flex-col gap-2 rounded-lg border border-[#EAF0FF] bg-white px-8 py-6 text-center">
		<div className="text-lg font-semibold leading-6 ">{title}</div>
		{!hideStar ? (
			<div className="inline-flex justify-center gap-1">
				<img
					width={13.87}
					height={13}
					src="/img/star.svg"
					alt="hvězda"
					className="ml-1.5 mr-1"
				/>
				<div className="font-medium text-primary">{rating}</div>
			</div>
		) : (
			<div className="font-medium text-primary">{rating}</div>
		)}
	</div>
)

const Review = ({
	date,
	text,
	communication,
	quality,
	overall,
	customerName,
}: {
	date: string
	text: string
	communication: number
	quality: number
	overall: number
	customerName: string
}) => {
	// Hopefully there won't be any Vietnamese reviewer
	const [firstname, surname] = customerName.split(' ')
	const displayName = `${firstname} ${surname.slice(0, 1)}.`

	return (
		<div className="mb-4">
			<div className="text-center font-semibold">
				{date}, {displayName}
			</div>
			<div className="text-center italic text-[#75809B]">„{text}“</div>
			<div className="flex w-full scale-75 flex-row gap-4">
				<RatingCard
					title="Komunikace"
					rating={(communication ?? 0).toFixed(1)}
				/>
				<RatingCard title="Kvalita úklidu" rating={(quality ?? 0).toFixed(1)} />
				<RatingCard title="Celkem" rating={(overall ?? 0).toFixed(1)} />
			</div>
		</div>
	)
}
